import { motion } from "framer-motion";
import { Leaf } from "lucide-react";
import { MEDIA, SUSTAINABILITY_POINTS } from "../../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Sustainability = () => {
  return (
    <section
      id="sustainability"
      data-testid="sustainability-section"
      className="scroll-mt-nav relative overflow-hidden bg-ink text-bone py-28 md:py-40"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={MEDIA.sustainability_bg}
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink" />
        <div className="absolute inset-0 grid-lines-dark opacity-40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <Leaf className="w-4 h-4 text-forest-accent" strokeWidth={1.5} />
              <span className="overline text-forest-accent">/ 06 — Clean Chemistry</span>
            </div>
            <h2
              className="font-display font-bold leading-[1] tracking-tight text-balance"
              style={{ fontSize: "clamp(2.25rem, 6vw, 5rem)" }}
            >
              Clean chemistry,
              <br />
              <span className="italic font-light text-bone/80">by design —</span>
              <br />
              not by accident.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-4 flex items-end">
            <p className="text-bone/70 text-base md:text-lg leading-relaxed">
              We operate a <span className="text-bone font-medium">Zero Liquid Discharge</span> facility — every drop
              of process water is recovered, treated and returned to the plant.
              Clean chemistry isn&apos;t a marketing line for us. It&apos;s how
              we&apos;re engineered.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {SUSTAINABILITY_POINTS.map((p, i) => (
            <motion.div
              key={p.k}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i}
              data-testid={`sustainability-${i}`}
              className={`bg-ink/60 backdrop-blur-md p-8 md:p-10 border border-white/5 relative ${
                i === 0 ? "ring-1 ring-forest-accent/60" : ""
              }`}
            >
              {i === 0 && (
                <div className="absolute -top-3 left-8 bg-forest-accent text-bone px-3 py-1 font-mono text-[10px] tracking-[0.22em] uppercase">
                  Flagship
                </div>
              )}
              <div className="font-display font-black text-bone/30 text-5xl md:text-6xl leading-none mb-8">
                {p.k}
              </div>
              <div className="font-display font-medium text-xl md:text-2xl leading-tight mb-4">
                {p.title}
              </div>
              <p className="text-bone/65 text-sm md:text-[15px] leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Pull quote */}
        <motion.blockquote
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-20 md:mt-28 max-w-4xl"
        >
          <p
            className="font-display font-light leading-[1.1] tracking-tight text-balance"
            style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.75rem)" }}
          >
            <span className="text-amber-accent">&ldquo;</span>
            We don&apos;t just preserve food. We preserve the systems
            food comes from — feedstock, supply chains, water and trust.
            <span className="text-amber-accent">&rdquo;</span>
          </p>
          <footer className="mt-6 overline text-bone/60">
            — Operations Charter, Milestone Preservatives
          </footer>
        </motion.blockquote>
      </div>
    </section>
  );
};
